export type CueKind = "correct" | "wrong" | "stable" | "commit";

const FREQ: Record<CueKind, number> = {
  commit: 220,
  correct: 523,
  wrong: 196,
  stable: 659,
};

export interface SoundDeps {
  getContext: () => AudioContext | null;
  isEnabled: () => boolean;
  getVolume: () => number;
}

export interface SoundService {
  playCue: (kind: CueKind) => void;
}

export function createSoundService(deps: SoundDeps): SoundService {
  return {
    playCue(kind) {
      if (!deps.isEnabled()) return;
      const ctx = deps.getContext();
      if (!ctx) return;
      if (ctx.state === "suspended") void ctx.resume();

      const now = ctx.currentTime;
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      const vol = Math.max(0, Math.min(1, deps.getVolume())) * 0.5;

      osc.type = "sine";
      osc.frequency.setValueAtTime(FREQ[kind], now);
      gain.gain.setValueAtTime(0.0001, now);
      gain.gain.exponentialRampToValueAtTime(Math.max(0.0002, vol), now + 0.01);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.13);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(now);
      osc.stop(now + 0.14);
    },
  };
}

let sharedContext: AudioContext | null = null;

export function getSharedAudioContext(): AudioContext | null {
  if (typeof window === "undefined") return null;
  const Ctor = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!Ctor) return null;
  if (!sharedContext) sharedContext = new Ctor();
  return sharedContext;
}
